import classcat from "classcat"
import capitalize from "lodash-es/capitalize"
import React from "react"

import commonStyles from "../ProjectBudgetForm.module.css"
import styles from "./BudgetEditor.module.css"

import { formatCurrency } from "~/helpers/CurrencyHelper"
import { formatMinutesToDays } from "~/helpers/budgetHelper"
import { OTHER_EXPENSES_ROLE, extractRoles } from "~/helpers/otherCosts"
import { getDefaultRate } from "~/helpers/rate-helpers"
import { formatMinuteToHours } from "~/helpers/time"

import DropdownSelector from "~/common/DropdownSelector"

import { RATE_TYPES, RateTypeSelection } from "~/GLOBALVARS"
import BudgetItemRow from "~/forms/ProjectBudgetForm/Budget/BudgetItemRow"
import OtherExpensesRow from "~/forms/ProjectBudgetForm/Budget/OtherExpensesRow"
import RoleAdder from "~/forms/ProjectBudgetForm/Budget/RoleAdder"
import TableInfo from "~/forms/ProjectBudgetForm/TableInfo"

import Column from "../TableColumn"
import Row from "../TableRow"
import { BudgetMethod, BudgetedRate, BudgetedRole, RateCard } from "../types"

type Props = {
  projectId: number
  rateType: RateTypeSelection
  rateCard: RateCard
  showNonBillableView: boolean
  roles: ReadonlyArray<BudgetedRole>
  projectRates: ReadonlyArray<BudgetedRate>
  hideFinancials?: boolean
  defaultFullTimeMinutes: number
  budgetMethod: BudgetMethod
  onUpdateRole: (role: BudgetedRole) => void
  onUpdateRates: (rate: BudgetedRate[]) => void
  onDeleteRole: (roleId: number) => void
  onChangeRateType: (rateType: RateTypeSelection) => void
  expensesAllocationDifference: number
  expensesBudget: number
  totalAllocated: number
  unallocatedRoles: Array<BudgetedRole>
  onAddRole: (id: number) => void
  onShowAllRoles: () => void
}

const BudgetEditor = (props: Props) => {
  const {
    projectId,
    rateCard,
    rateType,
    hideFinancials = true,
    showNonBillableView = true,
    roles,
    projectRates,
    defaultFullTimeMinutes,
    budgetMethod,
    onUpdateRole,
    onUpdateRates,
    onDeleteRole,
    onChangeRateType,
    expensesAllocationDifference,
    expensesBudget,
    totalAllocated,
    unallocatedRoles,
    onAddRole,
    onShowAllRoles,
  } = props

  const showDayView = rateType.value === "days"

  const roleData = extractRoles(roles)
  const expenseRole = roleData.expenses

  const totalMinutes = roleData.people.reduce(
    (prev, curr) =>
      curr.estimatedMinutes ? prev + curr.estimatedMinutes : prev,
    0,
  )

  const handleUpdateExpenses = (val: number) => {
    if (expenseRole) {
      const updatedExpenses = {
        ...expenseRole,
        estimatedMinutes: val,
      }
      onUpdateRole(updatedExpenses)
    }
  }

  const handleRemoveExpenses = () => {
    onDeleteRole(OTHER_EXPENSES_ROLE.id)
  }

  const showTotal = !!totalAllocated || !!totalMinutes

  return (
    <div className={commonStyles.budgetEditorWrapper}>
      <Row heading>
        <Column>Role</Column>
        {!hideFinancials && !showNonBillableView && (
          <Column className={commonStyles.alignRight}>Project Rate</Column>
        )}
        <Column className={commonStyles.alignRight}>
          <DropdownSelector
            selectedValue={rateType.value}
            options={RATE_TYPES}
            onChange={onChangeRateType}
            dataTestId="project-rate-types"
          />
        </Column>
        {!hideFinancials && !showNonBillableView && (
          <Column className={commonStyles.alignRight}>
            {capitalize(budgetMethod)} Budget
          </Column>
        )}
      </Row>
      <div className={commonStyles.tableBody}>
        {roleData.people.map((role) => {
          const project_rate = projectRates.find(
            (rate) => rate.roleId === role.id,
          )
          const rateCardRate = getDefaultRate(rateCard, role.id)
          const canDelete = !role.isScheduled
          return (
            <BudgetItemRow
              key={role.id}
              projectId={projectId}
              budgetedRole={role}
              removeRole={onDeleteRole}
              onRoleChange={onUpdateRole}
              onRatesUpdate={onUpdateRates}
              project_rate={project_rate}
              rate_card_rate={rateCardRate}
              hideFinancials={hideFinancials || showNonBillableView}
              showDayView={showDayView}
              default_full_time_minutes={defaultFullTimeMinutes}
              canDelete={canDelete}
              deleteTooltip={
                canDelete
                  ? `Remove budget allocation for "${role.name}"`
                  : `Roles that are currently scheduled cannot be removed from the budget allocation.`
              }
            />
          )
        })}
        {!hideFinancials && expenseRole && (
          <OtherExpensesRow
            key={expenseRole.id}
            value={expenseRole.estimatedMinutes}
            canDelete={!expenseRole.isScheduled}
            onUpdate={handleUpdateExpenses}
            onDelete={handleRemoveExpenses}
            deleteTooltip={
              !expenseRole.isScheduled
                ? "Remove budget allocation for other expenses"
                : "You cannot remove this from your budget because you already have other expenses recorded for this project."
            }
            expensesAllocationDifference={expensesAllocationDifference}
            expensesBudget={expensesBudget}
          />
        )}
      </div>
      {unallocatedRoles.length > 0 && (
        <div className={commonStyles.tableWidget}>
          <RoleAdder
            dataTest="add-budget-roles"
            roles={unallocatedRoles}
            onAddRole={onAddRole}
            onShowAllRoles={onShowAllRoles}
          />
        </div>
      )}
      {showTotal && (
        <TableInfo
          sticky
          className={classcat([
            commonStyles.totalsWrapper,
            styles.totalsWrapper,
            {
              [styles.nbView]: showNonBillableView,
            },
          ])}
          offset={0}
          dataTest="budget-allocation"
        >
          <div className={styles.textAlignLeft}>Total Role Budget</div>
          <div key="time" data-test="time-total">
            {showDayView
              ? formatMinutesToDays(totalMinutes, defaultFullTimeMinutes)
              : formatMinuteToHours(totalMinutes, "")}
          </div>
          {!showNonBillableView && (
            <div key="money" data-test="budget-total">
              {formatCurrency(totalAllocated)}
            </div>
          )}
        </TableInfo>
      )}
    </div>
  )
}

export default BudgetEditor
